import { useEffect, useRef } from 'react';

/**
 * Idle sign-out (ADR 238, Q-05): the clinic portal signs out after a period
 * without activity (6 hours since 2026-10-31; it was 30 minutes). The last activity is kept in local storage so every tab of
 * the portal shares it, and a laptop that slept past the limit signs out at once
 * when it wakes up. Only a timestamp is stored, never anything about the person.
 */

export const IDLE_KEY = 'laura.lastActivity';
const EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
/** Activity is written at most this often, so typing does not hammer storage. */
const WRITE_EVERY_MS = 15_000;
const CHECK_EVERY_MS = 30_000;

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function defaultStore(): Store | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

/** Last activity (ms) from storage; undefined when unknown or unreadable. */
export function lastActivity(store: Store | undefined): number | undefined {
  try {
    const value = Number(store?.getItem(IDLE_KEY));
    return Number.isFinite(value) && value > 0 ? value : undefined;
  } catch {
    return undefined;
  }
}

/** Forgets the last activity (on sign-out), so the next sign-in starts fresh. */
export function clearActivity(store: Store | undefined = defaultStore()): void {
  try {
    store?.removeItem(IDLE_KEY);
  } catch {
    // storage blocked: nothing was stored
  }
}

/** Whether the session has been idle for longer than `limitMs`. */
export function isIdle(last: number | undefined, now: number, limitMs: number): boolean {
  return last !== undefined && now - last >= limitMs;
}

export interface IdleOptions {
  /** Minutes without activity before `onIdle`; 0 or undefined turns it off. */
  minutes: number | undefined;
  /** Only tracked while true (signed in). */
  active: boolean;
  onIdle: () => void;
  store?: Store | undefined;
  now?: () => number;
}

export function useIdleSignOut({
  minutes,
  active,
  onIdle,
  store = defaultStore(),
  now = Date.now,
}: IdleOptions): void {
  const fired = useRef(false);
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!active || !minutes || minutes <= 0) return;
    const limit = minutes * 60_000;
    fired.current = false;
    let written = 0;

    const fire = () => {
      if (fired.current) return;
      fired.current = true;
      onIdleRef.current();
    };
    const check = () => {
      if (isIdle(lastActivity(store), now(), limit)) fire();
    };
    const touch = () => {
      const t = now();
      if (t - written < WRITE_EVERY_MS) return;
      // Activity after the limit does not revive a session that is already idle.
      if (isIdle(lastActivity(store), t, limit)) return fire();
      written = t;
      try {
        store?.setItem(IDLE_KEY, String(t));
      } catch {
        // storage blocked: this tab cannot track idle time, so it never signs out for it
      }
    };

    // A stored time from before this page load decides first (a laptop that slept,
    // a tab reopened later). Signing out clears it, so a fresh sign-in starts anew.
    check();
    if (fired.current) return;
    if (lastActivity(store) === undefined) touch();

    for (const e of EVENTS) window.addEventListener(e, touch, { passive: true });
    const onVisible = () => document.visibilityState === 'visible' && check();
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(check, CHECK_EVERY_MS);
    return () => {
      for (const e of EVENTS) window.removeEventListener(e, touch);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [active, minutes, store, now]);
}
